import React, { Component, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import Loading from "@/components/Loading";
import Dashboard from "./pages/dashboard/Dashboard.tsx";
import { EventBus, EventBusEvents } from "@/store/event-bus";
import Api from "@/store/api";
import Branding from "@/utils/branding";
import { constants, isDev } from "@/constants.js";

import ChatbotDesign from "@/pages/chatbot/design/ChatbotDesign";
import ChatBotSettings from "@/pages/chatbot/settings/ChatBotSettings";
import ChatBotSources from "@/pages/chatbot/sources/ChatBotSources.tsx";

import i18n from "i18next";
import i18next from "i18next";
import { initReactI18next, I18nextProvider } from "react-i18next";
import HttpApi from "i18next-http-backend";

import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { QueryClient, QueryClientProvider } from "react-query";
import ConversationsPage from "@/pages/chat";
import ChatBotAiSettings from "@/pages/chatbot/ai_settings/ChatBotAiSettings";
import ChatBotEmbed from "@/pages/chatbot/site-integration/ChatBotEmbed";
import CustomChatEditor from "@/components/chat/components/CustomChatEditor";
import ShadLayout from "@/components/layout/ShadLayout.tsx";

// Providers
import { UserProvider } from "@/store/context/UserContext.tsx";
import { ThemeProvider } from "@/components/shadcn/theme-provider.tsx";
import LeadsPage from "@/pages/chatbot/leads/LeadsPage.tsx";
import Playground from "@/pages/chatbot/playground/Playground.tsx";
import CatalogSettings from "@/pages/chatbot/catalog/CatalogSettings.tsx";
import PactIntegration from "@/pages/chatbot/integrations/Pact.tsx";
import { ProfilePageSwitcher } from "@/pages/profile/ProfilePageSwitcher.tsx";
import TelegramIntegration from "@/pages/chatbot/integrations/Telegram.tsx";
import { ChatbotIntegrations } from "@/pages/chatbot/site-integration/ChatbotIntegrations.tsx";
import { SidebarProvider } from "./components/shadcn/sidebar";
import { AffiliatePage } from "@/pages/profile/AffiliatePage.tsx";
import { SubscriptionCloudPayments } from "@/pages/profile/SubscriptionCloudPayments.tsx";
import HomePage from "@/pages/home/HomePage.tsx";
import PlaygroundPage from "@/pages/playground/Playground.tsx";

export const BugsnagApikey = "29242fdcc2fedc754bb7ca68b98448b0";

const retryImport = (fn: any, retriesLeft = 5, interval = 500) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: any) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retryImport(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

//Lazy components
const importLoginPageStars = () =>
  retryImport(() => import("@/pages/auth/LoginPageStars.tsx"));
const LoginPageStars = lazy(importLoginPageStars as any);
importLoginPageStars();

const importChatMainPage = () =>
  retryImport(() => import("@/components/chat/components/ChatMainPage.tsx"));
const ChatMainPage = lazy(importChatMainPage as any);
importChatMainPage();

Bugsnag.start({
  apiKey: isDev() ? "undefined" : BugsnagApikey,
  plugins: [new BugsnagPluginReact()],
});
const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);

const queryClient = new QueryClient();
const $crisp: any = (window as any).$crisp;

i18n
  .use(initReactI18next)
  .use(HttpApi)
  .init({
    backend: {
      loadPath: `${constants.baseURL}/locales/{{lng}}.json?v=30`,
    },
    lng: Branding().language,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

import { addAPIProvider } from "@iconify/react";

addAPIProvider("icon-server", {
  resources: [`${constants.url.API_PREFIX}icons`],
});

if ((window as any).hideInitialLoader) {
  (window as any).hideInitialLoader();
}

const withNavigate = (Component) => (props) => {
  const navigate = useNavigate();
  const params = useParams();
  return <Component {...props} navigate={navigate} params={params} />;
};

class App extends Component {
  state = {
    chatbotID: "",
    loggedIn: false,
  };

  constructor(props: any) {
    super();

    const bus = EventBus.getInstance();
    bus.register(EventBusEvents.loggedOut, () => {
      this.props.navigate("/login");
    });

    bus.register(EventBusEvents.loggedIn, () => {
      this.props.navigate("/dashboard");
    });

    bus.register(EventBusEvents.chatbotChanged, (data: any) => {
      this.setState({ chatbotID: data.id });
      this.props.navigate(`/chatbot/${data.id}/design`);
    });

    const urlParams = new URLSearchParams(window.location.search); // TODO: может уже надо убрать
    if (urlParams.has("checklogin")) {
      Api.dispatchSettingsEvent();
    }

    EventBus.getInstance().register(EventBusEvents.settingsUpdated, (data) => {
      if (data.email && $crisp) {
        setTimeout(() => {
          $crisp.push(["set", "user:email", [data.email]]);
        }, 1000);
      }
    });
  }

  render = () => {
    if (
      window.localStorage.getItem("redirect-command") !== null &&
      this.props
    ) {
      this.props.navigate("/profile");
    }

    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <I18nextProvider i18n={i18next}>
              <ThemeProvider defaultTheme="light" storageKey="theme">
                <SidebarProvider>
                  <main id="modal-root" className="relative w-full">
                    <Routes>
                      <Route
                        path="/login"
                        element={
                          <Suspense fallback={<Loading />}>
                            <LoginPageStars />
                          </Suspense>
                        }
                      />

                      <Route
                        exact
                        path="/"
                        element={<Navigate to="/dashboard" />}
                      />

                      <Route path="/*" element={<ShadLayout />}>
                        <Route path="dashboard" element={<HomePage />} />
                        <Route path="analytics" element={<Dashboard />} />
                        <Route
                          path="profile"
                          element={<ProfilePageSwitcher />}
                        />
                        <Route
                          path="profile2"
                          element={<SubscriptionCloudPayments />}
                        />
                        <Route
                          path="profile/invite"
                          element={<AffiliatePage />}
                        />
                        <Route
                          path="chatbot/:chatbotID/settings"
                          element={
                            <ChatBotSettings key={this.state.chatbotID} />
                          }
                        />
                        <Route
                          path="chatbot/:chatbotID/ai_settings"
                          element={
                            <ChatBotAiSettings key={this.state.chatbotID} />
                          }
                        />
                        {/*Integrations*/}
                        <Route
                          path="chatbot/:chatbotID/integrations/pact"
                          element={
                            <PactIntegration key={this.state.chatbotID} />
                          }
                        />
                        <Route
                          path="chatbot/:chatbotID/integrations/telegram"
                          element={
                            <TelegramIntegration key={this.state.chatbotID} />
                          }
                        />
                        <Route
                          path="chatbot/:chatbotID/publish_chatbot"
                          element={<ChatBotEmbed key={this.state.chatbotID} />}
                        />
                        <Route
                          path="chatbot/:chatbotID/leads"
                          element={<LeadsPage />}
                        />
                        <Route
                          path="chatbot/:chatbotID/catalog-settings"
                          element={<CatalogSettings />}
                        />
                      </Route>

                      <Route path="/*" element={<ShadLayout fullWidth />}>
                        <Route path="playground" element={<PlaygroundPage />} />
                      </Route>

                      <Route path="/*" element={<ShadLayout fullWidth />}>
                        <Route
                          path="chatbot/:chatbotID/sources/:source"
                          element={
                            <ChatBotSources key={this.state.chatbotID} />
                          }
                        />
                        <Route
                          path="chatbot/:chatbotID/conversations/*"
                          element={
                            <ConversationsPage key={this.state.chatbotID} />
                          }
                        />
                        <Route
                          path="chatbot/:chatbotID/design"
                          element={<ChatbotDesign key={this.state.chatbotID} />}
                        />
                        <Route
                          path="chatbot/:chatbotID/playground"
                          element={<Playground key={this.state.chatbotID} />}
                        />
                      </Route>

                      {/*NEW CHATGPT INTERFACE*/}
                      <Route path="/*" element={<ShadLayout fullWidth />}>
                        <Route path="chat/" element={<ChatMainPage />} />
                        <Route
                          path="chat/c/:chatID"
                          element={<ChatMainPage />}
                        />
                        {/*// Use the wrapper for new routes*/}
                        <Route path="chat/g/:gid" element={<ChatMainPage />} />
                        <Route
                          path="chat/g/:gid/c/:id"
                          element={<ChatMainPage />}
                        />
                        <Route
                          path="chat/custom/editor"
                          element={<CustomChatEditor />}
                        />
                        <Route
                          path="chat/custom/editor/:id"
                          element={<CustomChatEditor />}
                        />
                      </Route>
                    </Routes>
                  </main>
                </SidebarProvider>
              </ThemeProvider>
            </I18nextProvider>
          </UserProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  };
}

export default withNavigate(App);
